/**
 * Menu completo da aplicação: todos os links, por área, conforme o nível de acesso — Optimale Grid (frontend).
 *
 * Criado em: 17/10/2026 · Última modificação: 26/10/2026 · Versão: 1.14.0
 *
 * Retorno do responsável do produto (17/10/2026): "precisamos de um local com todos os links, e esses links vão
 * aparecer de acordo com o nível de acesso do usuário". Abre pelo botão ☰ da barra (painel lateral) e também em /menu.
 * A tela só esconde o que o perfil não usa; quem decide de fato é o backend.
 * D-010: nomes das áreas pela fonte única `nomes.ts` (o nome do menu é o padrão do sistema).
 */
import { Link } from "react-router-dom";
import type { Usuario } from "./api";
import { NOMES } from "./nomes";
import { pode } from "./permissoes";
import { marcaAtual } from "./marca";

type Item = { para: string; texto: string; descricao: string; visivel?: (u: Usuario) => boolean };
type Grupo = { titulo: string; itens: Item[] };

export const GRUPOS: Grupo[] = [
  { titulo: "Operação (CCO)", itens: [
    { para: "/cco", texto: NOMES.visaoGeral, descricao: "Ao vivo: cidades em faixas (Clássico), sala 3D ou Apresentação; zoom até o diagnóstico" },
    { para: "/cco/tendencias", texto: NOMES.tendencias, descricao: "Painéis do Grafana da operadora; clique num gráfico para tela cheia" },
    { para: "/cco/alarmes", texto: NOMES.alarmes, descricao: "Por prioridade, com causa provável, ação recomendada e reconhecimento" },
    { para: "/cco/analise", texto: NOMES.analise, descricao: "Heatmap de 30 dias: energia, água, eficiência, falhas, pressão e nível" },
    { para: "/cco/relatorios", texto: NOMES.relatorios, descricao: "Por unidade: energia, água, vazão, eficiência, pressão e alarmes, com destaques (CSV)" },
    { para: "/cco/qualidade", texto: NOMES.qualidade, descricao: "Saúde de cada sensor: sem comunicação, suspeito, travado ou saturado, e os ajustes" },
    { para: "/cco/gestor", texto: NOMES.gestor, descricao: "Rondas e diagnósticos da operação" },
    { para: "/cco/mapa", texto: NOMES.mapaComando, descricao: "Maquete 3D viva da rede: equipamentos com telemetria, agentes de IA, pessoas e alarmes", visivel: pode.verObservabilidadeLocal },
    { para: "/cco/seguranca", texto: NOMES.seguranca, descricao: "Eventos do agente de cibersegurança e revogação de dispositivos" },
    { para: "/cco#assistente", texto: NOMES.assistente, descricao: "Perguntas em linguagem natural sobre a operação agora" },
  ] },
  { titulo: "Rede e análise", itens: [
    { para: "/scada3d", texto: NOMES.scada3d, descricao: "A rede em 3D com os dados ao vivo e o agente de cada setor" },
    { para: "/rede", texto: NOMES.rede, descricao: "Mapa com relevo e a simulação hidráulica de 24 h pela NBR 12218" },
  ] },
  { titulo: NOMES.cadastro, itens: [
    { para: "/cadastro", texto: "Todos os ativos", descricao: "Equipamentos da rede, fichas técnicas e histórico", visivel: pode.verCadastro },
    { para: "/cadastro?situacao=pendente", texto: "Pendentes de validação", descricao: "Importados do DWG ou cadastrados em campo", visivel: pode.verCadastro },
    { para: "/cadastro/novo", texto: "Novo ativo", descricao: "Cadastrar um equipamento com a posição no mapa",
      visivel: (u) => pode.editarCadastro(u) || pode.cadastrarEmCampo(u) },
    { para: "/cadastro/importar", texto: "Importar DWG/DXF", descricao: "Prévia, conferência do fuso e importação da rede", visivel: pode.editarCadastro },
  ] },
  { titulo: "Painéis e agentes", itens: [
    { para: "/meus-paineis", texto: NOMES.paineis, descricao: "Painéis pessoais, da organização e aprovação do compartilhamento", visivel: pode.verPaineis },
    { para: "/treinamento", texto: NOMES.treinamento, descricao: `Perfis, versões, testes, publicação e sugestões (equipe ${marcaAtual().empresa})`, visivel: pode.verTreinamento },
  ] },
  // US-1804 (ADR-0014): back-office — cada item aparece só para quem tem a capacidade
  { titulo: NOMES.administracao, itens: [
    { para: "/admin/usuarios", texto: NOMES.usuarios, descricao: "Cadastro da equipe com convite por e-mail, papéis, horário de acesso e histórico",
      visivel: pode.verUsuarios },
    { para: "/admin/empresas", texto: NOMES.empresas, descricao: `Operadoras clientes: registro, CNPJ, contato e situação (cadastro estrutural da ${marcaAtual().empresa})`,
      visivel: pode.verEstrutura },
    { para: "/admin/canais", texto: NOMES.canais, descricao: "Quem da equipe recebe os alertas no Telegram/WhatsApp e os e-mails e webhooks das notificações",
      visivel: pode.verUsuarios },
    { para: "/admin/conectores", texto: NOMES.conectores, descricao: "Integrações por API (ex.: Costa Rica): configuração, token cifrado, teste e ativação",
      visivel: pode.verEstrutura },
    { para: "/admin/feriados", texto: NOMES.feriados, descricao: "Feriados nacionais (planilha da ANBIMA), estaduais, municipais e dias atípicos que o perfil do dia da semana deixa de fora",
      visivel: (u) => pode.editarFeriados(u) || pode.importarFeriadosNacionais(u) },
    { para: "/admin/referencias", texto: NOMES.referencias, descricao: "Dados oficiais dos municípios (SNIS/SINISA, outorgas e Atlas da ANA, chuva) importados do arquivo baixado",
      visivel: (u) => pode.importarReferencias(u) || pode.verEstrutura(u) },
    { para: "/admin/marca", texto: NOMES.marca, descricao: "Nome, logomarca e cores do integrador; publicar, histórico e pacote (administrador da plataforma)",
      visivel: pode.verMarca },
    { para: "/admin/dispositivos", texto: NOMES.dispositivos, descricao: `CLP/RTU de campo: localização, tipo, realocação e desativação (cadastro estrutural da ${marcaAtual().empresa})`,
      visivel: pode.verEstrutura },
  ] },
  { titulo: "Configuração", itens: [
    { para: "/manobra-automatica", texto: NOMES.manobraAutomatica, descricao: "Autorizações (com validade) para o motor executar manobras de regras; só supervisor e coordenador concedem" },
    { para: "/regras", texto: NOMES.regras, descricao: "Limites e alarmes da operação: modelos (NBR 12218, reservatório, motor), simulação nas últimas 24 h e versões" },
    { para: "/videowall", texto: NOMES.videowall, descricao: "De 1 a 12 telas, em um ou vários monitores; a engenharia configura" },
  ] },
];

export function MenuCompleto({ usuario, aoEscolher }: { usuario: Usuario; aoEscolher?: () => void }) {
  return (
    <nav className="menu-completo" aria-label="Menu completo">
      {GRUPOS.map((g) => {
        const itens = g.itens.filter((i) => !i.visivel || i.visivel(usuario));
        if (!itens.length) return null;
        return (
          <section key={g.titulo}>
            <h3>{g.titulo}</h3>
            <ul>
              {itens.map((i) => (
                <li key={i.para}>
                  <Link to={i.para} onClick={aoEscolher}><b>{i.texto}</b><small className="nx-suave">{i.descricao}</small></Link>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </nav>
  );
}
